import type { Spell } from '@character-creator/types';

export const level7Spells: Spell[] = [
  {
    id: 'conjure-celestial',
    name: 'Conjure Celestial',
    level: 7,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 minute',
    range: '90 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 hour',
    concentration: true,
    description: "You summon a celestial of challenge rating 4 or lower, which appears in an unoccupied space that you can see within range. The celestial disappears when it drops to 0 hit points or when the spell ends. The celestial is friendly to you and your companions for the duration.",
    higherLevel: "When you cast this spell using a 9th-level spell slot, you summon a celestial of challenge rating 5 or lower.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'delayed-blast-fireball',
    name: 'Delayed Blast Fireball',
    level: 7,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '150 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a tiny ball of bat guano and sulfur' },
    duration: '1 minute',
    concentration: true,
    description: "A beam of yellow light flashes from your pointing finger, then condenses to linger at a chosen point within range as a glowing bead for the duration. When the spell ends, either because your concentration is broken or because you decide to end it, the bead blossoms with a low roar into an explosion of flame that spreads around corners. Each creature in a 20-foot-radius sphere centered on that point must make a Dexterity saving throw. A creature takes fire damage equal to the total accumulated damage on a failed save, or half as much damage on a successful one. The spell's base damage is 12d6. If at the end of your turn the bead has not yet detonated, the damage increases by 1d6.",
    higherLevel: "When you cast this spell using a spell slot of 8th level or higher, the base damage increases by 1d6 for each slot level above 7th.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'divine-word',
    name: 'Divine Word',
    level: 7,
    school: 'evocation',
    ritual: false,
    castingTime: '1 bonus action',
    range: '30 feet',
    components: { verbal: true, somatic: false, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You utter a divine word, imbued with the power that shaped the world at the dawn of creation. Choose any number of creatures you can see within range. Each creature that can hear you must make a Charisma saving throw. On a failed save, a creature suffers an effect based on its current hit points: 50 hp or fewer: deafened for 1 minute; 40 hp or fewer: deafened and blinded for 10 minutes; 30 hp or fewer: blinded, deafened, and stunned for 1 hour; 20 hp or fewer: killed instantly. Regardless of its current hit points, a celestial, an elemental, a fey, or a fiend that fails its save is forced back to its plane of origin.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'etherealness',
    name: 'Etherealness',
    level: 7,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: false },
    duration: '8 hours',
    concentration: false,
    description: "You step into the border regions of the Ethereal Plane, in the area where it overlaps with your current plane. You remain in the Border Ethereal for the duration or until you use your action to dismiss the spell. During this time, you can move in any direction. If you move up or down, every foot of movement costs an extra foot. You can see and hear the plane you originated from, but everything there looks gray, and you can't see anything more than 60 feet away.",
    higherLevel: "When you cast this spell using a spell slot of 8th level or higher, you can target up to three willing creatures (including you) for each slot level above 7th.",
    classes: ['bard', 'cleric', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'finger-of-death',
    name: 'Finger of Death',
    level: 7,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You send negative energy coursing through a creature that you can see within range, causing it searing pain. The target must make a Constitution saving throw. It takes 7d8 + 30 necrotic damage on a failed save, or half as much damage on a successful one. A humanoid killed by this spell rises at the start of your next turn as a zombie that is permanently under your command, following your verbal orders to the best of its ability.",
    classes: ['sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'fire-storm',
    name: 'Fire Storm',
    level: 7,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '150 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "A storm made up of sheets of roaring flame appears in a location you choose within range. The area of the storm consists of up to ten 10-foot cubes, which you can arrange as you wish. Each cube must have at least one face adjacent to the face of another cube. Each creature in the area must make a Dexterity saving throw. It takes 7d10 fire damage on a failed save, or half as much damage on a successful one. The fire damages objects in the area and ignites flammable objects that aren't being worn or carried. If you choose, plant life in the area is unaffected by this spell.",
    classes: ['cleric', 'druid', 'sorcerer'],
    source: 'srd-5.1',
  },
  {
    id: 'forcecage',
    name: 'Forcecage',
    level: 7,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '100 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'ruby dust worth 1,500 gp', materialCost: 1500 },
    duration: '1 hour',
    concentration: false,
    description: "An immobile, invisible, cube-shaped prison composed of magical force springs into existence around an area you choose within range. The prison can be a cage or a solid box as you choose. A prison in the shape of a cage can be up to 20 feet on a side and is made from 1/2-inch diameter bars spaced 1/2 inch apart. A prison in the shape of a box can be up to 10 feet on a side, creating a solid barrier that prevents any matter from passing through it and blocking any spells cast into or out from the area.",
    classes: ['bard', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'mirage-arcane',
    name: 'Mirage Arcane',
    level: 7,
    school: 'illusion',
    ritual: false,
    castingTime: '10 minutes',
    range: 'Sight',
    components: { verbal: true, somatic: true, material: false },
    duration: '10 days',
    concentration: false,
    description: "You make terrain in an area up to 1 mile square look, sound, smell, and even feel like some other sort of terrain. The terrain's general shape remains the same, however. Open fields or a road could be made to resemble a swamp, hill, crevasse, or some other difficult or impassable terrain. A pond can be made to seem like a grassy meadow, a precipice like a gentle slope, or a rock-strewn gully like a wide and smooth road.",
    classes: ['bard', 'druid', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'mordenkainens-sword',
    name: "Arcane Sword",
    level: 7,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a miniature platinum sword with a grip and pommel of copper and zinc, worth 250 gp', materialCost: 250 },
    duration: '1 minute',
    concentration: true,
    description: "You create a sword-shaped plane of force that hovers within range. It lasts for the duration. When the sword appears, you make a melee spell attack against a target of your choice within 5 feet of the sword. On a hit, the target takes 3d10 force damage. Until the spell ends, you can use a bonus action on each of your turns to move the sword up to 20 feet to a spot you can see and repeat this attack against the same target or a different one.",
    classes: ['bard', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'plane-shift',
    name: 'Plane Shift',
    level: 7,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a forked, metal rod worth at least 250 gp, attuned to a particular plane of existence', materialCost: 250 },
    duration: 'Instantaneous',
    concentration: false,
    description: "You and up to eight willing creatures who link hands in a circle are transported to a different plane of existence. You can specify a target destination in general terms, and you appear in or near that destination. Alternatively, if you know the sigil sequence of a teleportation circle on another plane of existence, this spell can take you to that circle. You can use this spell to banish an unwilling creature to another plane. Choose a creature within your reach and make a melee spell attack against it. On a hit, the creature must make a Charisma saving throw.",
    classes: ['cleric', 'druid', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'prismatic-spray',
    name: 'Prismatic Spray',
    level: 7,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: 'Self (60-foot cone)',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "Eight multicolored rays of light flash from your hand. Each ray is a different color and has a different power and purpose. Each creature in a 60-foot cone must make a Dexterity saving throw. For each target, roll a d8 to determine which color ray affects it. Effects range from fire damage to poison damage to being sent to another plane.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'project-image',
    name: 'Project Image',
    level: 7,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: '500 miles',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a small replica of you made from materials worth at least 5 gp', materialCost: 5 },
    duration: '1 day',
    concentration: true,
    description: "You create an illusory copy of yourself that lasts for the duration. The copy can appear at any location within range that you have seen before, regardless of intervening obstacles. The illusion looks and sounds like you but is intangible. If the illusion takes any damage, it disappears, and the spell ends. You can use your action to move this illusion up to twice your speed, and make it gesture, speak, and behave in whatever way you choose.",
    classes: ['bard', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'regenerate',
    name: 'Regenerate',
    level: 7,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 minute',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a prayer wheel and holy water' },
    duration: '1 hour',
    concentration: false,
    description: "You touch a creature and stimulate its natural healing ability. The target regains 4d8 + 15 hit points. For the duration of the spell, the target regains 1 hit point at the start of each of its turns (10 hit points each minute). The target's severed body members (fingers, legs, tails, and so on), if any, are restored after 2 minutes. If you have the severed part and hold it to the stump, the spell instantaneously causes the limb to knit to the stump.",
    classes: ['bard', 'cleric', 'druid'],
    source: 'srd-5.1',
  },
  {
    id: 'resurrection',
    name: 'Resurrection',
    level: 7,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 hour',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a diamond worth at least 1,000 gp, which the spell consumes', materialCost: 1000, materialConsumed: true },
    duration: 'Instantaneous',
    concentration: false,
    description: "You touch a dead creature that has been dead for no more than a century, that didn't die of old age, and that isn't undead. If its soul is free and willing, the target returns to life with all its hit points. This spell neutralizes any poisons and cures normal diseases afflicting the creature when it died. It doesn't, however, remove magical diseases, curses, and the like; if such effects aren't removed prior to casting the spell, they afflict the target on its return to life.",
    classes: ['bard', 'cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'reverse-gravity',
    name: 'Reverse Gravity',
    level: 7,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '100 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a lodestone and iron filings' },
    duration: '1 minute',
    concentration: true,
    description: "This spell reverses gravity in a 50-foot-radius, 100-foot high cylinder centered on a point within range. All creatures and objects that aren't somehow anchored to the ground in the area fall upward and reach the top of the area when you cast this spell. A creature can make a Dexterity saving throw to grab onto a fixed object it can reach, thus avoiding the fall.",
    classes: ['druid', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'sequester',
    name: 'Sequester',
    level: 7,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a powder composed of diamond, emerald, ruby, and sapphire dust worth at least 5,000 gp, which the spell consumes', materialCost: 5000, materialConsumed: true },
    duration: 'Until dispelled',
    concentration: false,
    description: "By means of this spell, a willing creature or an object can be hidden away, safe from detection for the duration. When you cast the spell and touch the target, it becomes invisible and can't be targeted by divination spells or perceived through scrying sensors created by divination spells. If the target is a creature, it falls into a state of suspended animation. Time ceases to flow for it, and it doesn't grow older.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'simulacrum',
    name: 'Simulacrum',
    level: 7,
    school: 'illusion',
    ritual: false,
    castingTime: '12 hours',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'snow or ice in quantities sufficient to make a life-size copy of the duplicated creature; some hair, fingernail clippings, or other piece of that creature\'s body placed inside the snow or ice; and powdered ruby worth 1,500 gp, sprinkled over the duplicate and consumed by the spell', materialCost: 1500, materialConsumed: true },
    duration: 'Until dispelled',
    concentration: false,
    description: "You shape an illusory duplicate of one beast or humanoid that is within range for the entire casting time of the spell. The duplicate is a creature, partially real and formed from ice or snow, and it can take actions and otherwise be affected as a normal creature. It appears to be the same as the original, but it has half the creature's hit point maximum and is formed without any equipment. Otherwise, the illusion uses all the statistics of the creature it duplicates.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'symbol',
    name: 'Symbol',
    level: 7,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 minute',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'mercury, phosphorus, and powdered diamond and opal with a total value of at least 1,000 gp, which the spell consumes', materialCost: 1000, materialConsumed: true },
    duration: 'Until dispelled or triggered',
    concentration: false,
    description: "When you cast this spell, you inscribe a harmful glyph either on a surface (such as a section of floor, a wall, or a table) or within an object that can be closed to conceal the glyph (such as a book, a scroll, or a treasure chest). If you choose a surface, the glyph can cover an area of the surface no larger than 10 feet in diameter. If you choose an object, that object must remain in its place; if the object is moved more than 10 feet from where you cast this spell, the glyph is broken, and the spell ends without being triggered.",
    classes: ['bard', 'cleric', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'teleport',
    name: 'Teleport',
    level: 7,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '10 feet',
    components: { verbal: true, somatic: false, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "This spell instantly transports you and up to eight willing creatures of your choice that you can see within range, or a single object that you can see within range, to a destination you select. If you target an object, it must be able to fit entirely inside a 10-foot cube, and it can't be held or carried by an unwilling creature. The destination you choose must be known to you, and it must be on the same plane of existence as you.",
    classes: ['bard', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
];
